import React, { useState } from 'react';
import { Copy, Check, Mail, ArrowLeft, Zap, Bitcoin } from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import '../styles/Support.css';

const Support = () => {
  const navigate = useNavigate();
  const [copiedItem, setCopiedItem] = useState(null);

  const lightningAddress = 'jtbtc@walletofsatoshi.com';
  const onchainAddress = 'bc1plz92ctrmw329um56nmhtlnej5hx58p5afh0dcf7vrw67yvlyk0fs6wnepe';
  const supportEmail = 'jared@growyourbtc.com';

  const noteTemplate = `Supporting Grow Your BTC

Thank you for providing valuable Bitcoin education and resources. This contribution helps continue the mission of helping others secure, grow, and guard their Bitcoin.

- Supporter`;

  const handleCopy = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedItem(label);
      toast.success(`${label} copied to clipboard!`, {
        duration: 3000,
      });
      setTimeout(() => setCopiedItem(null), 2000);
    } catch (err) {
      toast.error('Failed to copy. Please try again.');
    }
  };

  const handleMobileWallet = (type) => {
    const url = type === 'lightning'
      ? `lightning:${lightningAddress}`
      : `bitcoin:${onchainAddress}`;
    window.location.href = url;
  };

  return (
    <div className="support-container">
      <header className="support-header">
        <div className="support-header-content">
          <div className="support-logo-container">
            <img
              src="/gybtc-logo.png"
              alt="Grow Your BTC Logo"
              className="support-logo"
            />
          </div>
          <button onClick={() => navigate('/')} className="support-back-btn">
            <ArrowLeft size={20} />
            Back to Home
          </button>
        </div>
      </header>

      <section className="support-hero">
        <div className="support-hero-bg-watermark">
          <Bitcoin size={350} strokeWidth={0.5} />
        </div>
        <div className="support-hero-content">
          <h1 className="support-hero-title">
            Support Grow Your BTC
          </h1>
          <p className="support-hero-subtitle">
            If my work has helped you understand Bitcoin, avoid mistakes, or move forward with more clarity,
            you can support the project with a tip or donation. Every satoshi helps continue the mission
            of empowering individuals with Bitcoin knowledge.
          </p>
        </div>
      </section>

      <section className="support-note-section">
        <div className="support-note-card">
          <h3 className="support-note-title">Optional: Include a Note</h3>
          <p className="support-note-description">
            Want to include a message with your support? Copy this template and paste it when sending.
          </p>
          <button
            onClick={() => handleCopy(noteTemplate, 'Note template')}
            className="support-btn-secondary"
          >
            {copiedItem === 'Note template' ? <Check size={18} /> : <Copy size={18} />}
            {copiedItem === 'Note template' ? 'Copied!' : 'Copy Note Template'}
          </button>
        </div>
      </section>

      <section className="support-methods">
        <div className="support-section-header">
          <h2 className="support-section-title">Choose How You'd Like to Support</h2>
          <p className="support-section-subtitle">
            Both methods work great. Lightning is fastest for small amounts with minimal fees.
          </p>
        </div>

        <div className="support-cards-grid">
          <div className="support-method-card">
            <div className="support-method-icon">
              <Zap size={56} />
            </div>
            <h3 className="support-method-title">Lightning Network</h3>
            <p className="support-method-description">
              Fast, instant, and low-fee. Perfect for smaller tips and recurring support.
            </p>

            <div className="support-address-container">
              <label className="support-address-label">Lightning Address</label>
              <div className="support-address-display">
                <code className="support-address-text">{lightningAddress}</code>
              </div>
            </div>

            <div className="support-btn-group">
              <button
                onClick={() => handleCopy(lightningAddress, 'Lightning address')}
                className="support-btn-primary"
              >
                {copiedItem === 'Lightning address' ? <Check size={20} /> : <Copy size={20} />}
                {copiedItem === 'Lightning address' ? 'Copied!' : 'Copy Address'}
              </button>
              <button
                onClick={() => handleMobileWallet('lightning')}
                className="support-btn-outline"
              >
                <Zap size={20} />
                Open Wallet
              </button>
            </div>
          </div>

          <div className="support-method-card">
            <div className="support-method-icon">
              <Bitcoin size={56} />
            </div>
            <h3 className="support-method-title">Bitcoin On-Chain</h3>
            <p className="support-method-description">
              Traditional Bitcoin layer. Best for larger amounts and long-term storage verification.
            </p>

            <div className="support-address-container">
              <label className="support-address-label">Bitcoin Address</label>
              <div className="support-address-display">
                <code className="support-address-text support-address-text-small">
                  {onchainAddress}
                </code>
              </div>
            </div>

            <div className="support-btn-group">
              <button
                onClick={() => handleCopy(onchainAddress, 'Bitcoin address')}
                className="support-btn-primary"
              >
                {copiedItem === 'Bitcoin address' ? <Check size={20} /> : <Copy size={20} />}
                {copiedItem === 'Bitcoin address' ? 'Copied!' : 'Copy Address'}
              </button>
              <button
                onClick={() => handleMobileWallet('bitcoin')}
                className="support-btn-outline"
              >
                <Bitcoin size={20} />
                Open Wallet
              </button>
            </div>
          </div>
        </div>
      </section>

      <section className="support-help-section">
        <div className="support-help-card">
          <div className="support-help-icon">
            <Mail size={40} />
          </div>
          <h3 className="support-help-title">Need Help or Have Questions?</h3>
          <p className="support-help-description">
            If you have questions about supporting the project or want to discuss collaboration opportunities,
            feel free to reach out directly.
          </p>
          <a
            href={`mailto:${supportEmail}`}
            className="support-btn-email"
          >
            <Mail size={20} />
            Email: {supportEmail}
          </a>
        </div>
      </section>

      <footer className="support-footer">
        <div className="support-footer-content">
          <p className="support-footer-text">
            Thank you for supporting Bitcoin education and helping others learn to secure their wealth.
          </p>
          <button onClick={() => navigate('/')} className="support-footer-link">
            <ArrowLeft size={16} />
            Return to Home
          </button>
        </div>
      </footer>
    </div>
  );
};

export default Support;
